import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdWork, MdPeople, MdWarning, MdRefresh, MdTrendingUp, MdTrendingDown } from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge/Badge";
import Avatar from "components/avatar";
import Button from "components/button";
import DataTable from "components/data-table";
import Skeleton from "components/skeleton/Skeleton";
import StatCard from "components/stat-card";
import Tabs, { TabPanel } from "components/tabs";
import { PageContainer, PageHeader } from "components/page";
import { EmptyState, ErrorState } from "components/states";
import { getAudience, profilePath } from "components/assessment/audiences";
import notify from "lib/notify";
import { getApiErrorMessage } from "api";
import { EMPTY } from "utils/format";

const TABS_ID = "role-fit-tabs";

const UTILIZATION_STATUS = {
  well_aligned: { tone: "success", label: "Well Aligned" },
  underutilized: { tone: "warning", label: "Underutilized" },
  misaligned: { tone: "warning", label: "Misaligned" },
  at_risk: { tone: "danger", label: "At Risk" },
};

const BURNOUT_TONES = { low: "success", moderate: "warning", high: "danger" };

const SEVERITY_TONES = { high: "danger", medium: "warning" };

const FIT_BAR = { success: "bg-success", warning: "bg-warning", danger: "bg-danger" };

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const fitTone = (score) => {
  if (score >= 80) return "success";
  if (score >= 60) return "warning";
  return "danger";
};

const personName = (p) => (p.user ? `${p.user.firstName} ${p.user.lastName}` : "Unknown");

const UtilizationBadge = ({ status }) => {
  const config = UTILIZATION_STATUS[status];
  return <Badge tone={config?.tone || "neutral"}>{config?.label || status}</Badge>;
};

const BurnoutBadge = ({ level }) => (
  <Badge tone={BURNOUT_TONES[level] || "neutral"}>{level ? capitalize(level) : "Unknown"}</Badge>
);

const FitScore = ({ score = 0 }) => (
  <div className="flex items-center gap-2">
    <div aria-hidden="true" className="h-2 w-16 rounded-full bg-surface-subtle">
      <div className={`h-2 rounded-full ${FIT_BAR[fitTone(score)]}`} style={{ width: `${score}%` }} />
    </div>
    <span className="text-sm font-medium text-fg">{score}%</span>
  </div>
);

const CardGridSkeleton = ({ columns }) => (
  <div className={`grid grid-cols-1 gap-5 ${columns}`}>
    {Array.from({ length: 4 }, (_, i) => (
      <Skeleton key={i} className="h-40 w-full rounded-card" />
    ))}
  </div>
);

export default function RoleFit({ audience }) {
  const config = getAudience(audience);
  const { labels } = config;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleProfiles, setRoleProfiles] = useState([]);
  const [utilizationData, setUtilizationData] = useState([]);
  const [ministryGaps, setMinistryGaps] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [stats, setStats] = useState({
    overallFit: 0,
    underUtilized: 0,
    overExtended: 0,
    openPositions: 0,
  });
  const [activeTab, setActiveTab] = useState("utilization");

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [rolesRes, insightsRes, gapsRes] = await Promise.all([
        config.service.getRoleProfiles(),
        config.service.getUtilizationInsights(),
        config.service.getMinistryGaps(),
      ]);

      if (rolesRes.success) {
        setRoleProfiles(rolesRes.data || []);
        const open = (rolesRes.data || []).filter((r) => r.isOpen).length;
        setStats((s) => ({ ...s, openPositions: open }));
      }

      if (insightsRes.success) {
        const insights = insightsRes.data || {};
        // Combine all insight categories into a single list for the table
        const combinedData = [
          ...(insights.misaligned || []).map((p) => ({
            ...p,
            userId: p.userId,
            name: personName(p),
            avatar: p.user?.avatar,
            utilizationStatus: "misaligned",
            roleFitScore: p.fitScore || 0,
            currentRole: p.currentRole,
            burnoutRiskLevel: "moderate",
            recommendation: "Consider role reassignment",
          })),
          ...(insights.underutilized || []).map((p) => ({
            ...p,
            userId: p.userId,
            name: personName(p),
            avatar: p.user?.avatar,
            utilizationStatus: "underutilized",
            roleFitScore: 70,
            burnoutRiskLevel: "low",
            recommendation: "Explore additional responsibilities",
          })),
          ...(insights.leadershipPotential || []).map((p) => ({
            ...p,
            userId: p.userId,
            name: personName(p),
            avatar: p.user?.avatar,
            utilizationStatus: "well_aligned",
            roleFitScore: 85,
            burnoutRiskLevel: "low",
            recommendation: "Consider for leadership development",
          })),
        ];
        setUtilizationData(combinedData);

        const underUtilized = (insights.underutilized || []).length;
        const overExtended = (insights.misaligned || []).length;
        const totalFit = combinedData.reduce((sum, d) => sum + (d.roleFitScore || 0), 0);
        const avgFit = combinedData.length > 0 ? Math.round(totalFit / combinedData.length) : 0;

        setStats((s) => ({
          ...s,
          overallFit: avgFit,
          underUtilized,
          overExtended,
        }));
      }

      if (gapsRes.success) {
        setMinistryGaps(gapsRes.data || []);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      await config.service.recalculateUtilization();
      notify.success("Utilization recalculated");
      fetchData();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not recalculate utilization"));
    } finally {
      setRecalculating(false);
    }
  };

  const utilizationColumns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar user={{ avatar: row.original.avatar }} name={row.original.name} size="md" />
            <p className="font-medium text-fg">{row.original.name}</p>
          </div>
        ),
      },
      {
        id: "currentRole",
        header: "Current Role",
        cell: ({ row }) => <span className="text-fg-muted">{row.original.currentRole || labels.role}</span>,
      },
      {
        id: "roleFit",
        header: "Role Fit",
        cell: ({ row }) => <FitScore score={row.original.roleFitScore || 0} />,
      },
      {
        id: "utilization",
        header: "Utilization",
        cell: ({ row }) => <UtilizationBadge status={row.original.utilizationStatus} />,
      },
      {
        id: "burnoutRisk",
        header: "Burnout Risk",
        cell: ({ row }) => <BurnoutBadge level={row.original.burnoutRiskLevel} />,
      },
      {
        id: "recommendation",
        header: "Recommendation",
        cell: ({ row }) => <span className="text-fg-muted">{row.original.recommendation || EMPTY}</span>,
      },
    ],
    [labels.role]
  );

  const tabs = [
    { id: "utilization", label: `${labels.adjective} Utilization` },
    { id: "roles", label: "Role Profiles" },
    { id: "gaps", label: "Ministry Gaps" },
  ];

  const renderUtilization = () => (
    <DataTable
      aria-label={`${labels.adjective} utilization`}
      columns={utilizationColumns}
      data={utilizationData}
      loading={loading}
      getRowId={(row, index) => `${row.utilizationStatus}-${row.id ?? index}`}
      onRowClick={(row) => navigate(profilePath(config, row.userId))}
      emptyState={
        <EmptyState
          icon={MdPeople}
          title="No Utilization Data Yet"
          description="Complete intake assessments to see insights."
        />
      }
    />
  );

  const renderRoles = () => {
    if (loading) return <CardGridSkeleton columns="md:grid-cols-2 lg:grid-cols-3" />;
    if (roleProfiles.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdWork}
            title="No Role Profiles Defined"
            description="Create role profiles to match people with ministry positions."
          />
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {roleProfiles.map((role) => (
          <Card key={role.id} padding="md">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-heading">{role.name}</h2>
              {role.isOpen && <Badge tone="success">Open</Badge>}
            </div>
            <p className="mb-3 text-sm text-fg-muted">{role.description || "No description"}</p>
            <div className="mb-3 flex items-center gap-2 text-sm text-fg-muted">
              <MdWork aria-hidden="true" className="h-4 w-4" />
              {role.ministryArea || "General"}
            </div>
            <p className="text-sm text-fg-muted">{role.candidateCount || 0} potential candidates</p>
          </Card>
        ))}
      </div>
    );
  };

  const renderGaps = () => {
    if (loading) return <CardGridSkeleton columns="md:grid-cols-2" />;
    if (ministryGaps.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdPeople}
            title="No Ministry Gaps Detected"
            description="All ministry areas appear to be adequately covered."
          />
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {ministryGaps.map((gap, idx) => (
          <Card key={idx} padding="md">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-heading">{gap.ministryArea}</h2>
              {gap.severity && (
                <Badge tone={SEVERITY_TONES[gap.severity] || "info"}>{capitalize(gap.severity)} Priority</Badge>
              )}
            </div>
            <div className="mb-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-fg-muted">Current {labels.plural}</p>
                <p className="text-lg font-bold text-fg">{gap.currentStaff || 0}</p>
              </div>
              <div>
                <p className="text-fg-muted">Needed</p>
                <p className="text-lg font-bold text-danger-fg">{gap.needed || 0}</p>
              </div>
            </div>
            <p className="text-sm text-fg-muted">
              {gap.recommendation || "Consider recruiting for this ministry area."}
            </p>
          </Card>
        ))}
      </div>
    );
  };

  const panels = { utilization: renderUtilization, roles: renderRoles, gaps: renderGaps };

  return (
    <PageContainer>
      <PageHeader
        title="Role Fit"
        description={`Analyze role fit and optimize ${labels.pluralLower} utilization`}
        actions={
          <Button
            variant="secondary"
            leftIcon={<MdRefresh aria-hidden="true" />}
            loading={recalculating}
            onClick={handleRecalculate}
          >
            Recalculate
          </Button>
        }
      />

      {error ? (
        <ErrorState message={getApiErrorMessage(error)} onRetry={fetchData} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={MdTrendingUp} accent="sage" label="Overall Fit" value={`${stats.overallFit}%`} loading={loading} />
            <StatCard
              icon={MdTrendingDown}
              accent="gold"
              label="Under-Utilized"
              value={String(stats.underUtilized)}
              loading={loading}
            />
            <StatCard
              icon={MdWarning}
              accent="danger"
              label="Over-Extended"
              value={String(stats.overExtended)}
              loading={loading}
            />
            <StatCard icon={MdWork} accent="sky" label="Open Roles" value={String(stats.openPositions)} loading={loading} />
          </div>

          <div className="flex flex-col gap-5">
            <Tabs
              id={TABS_ID}
              aria-label="Role fit sections"
              syncParam="tab"
              value={activeTab}
              onChange={setActiveTab}
              tabs={tabs}
            />
            <TabPanel tabsId={TABS_ID} tabId={activeTab}>
              {panels[activeTab]()}
            </TabPanel>
          </div>
        </>
      )}
    </PageContainer>
  );
}
